import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import TaskService from 'src/services/tasks/taskService';
import { MeasureUnit, Region, Task } from 'src/services/tasks/types';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';

// **** Constants **** //

const taskModalFieldLengthValues: TaskModalFieldLength = {
  maxTaskNameCharLength: 255,
  maxDescriptionCharLength: 100,
};

const initialState: TaskConfigPageState = {
  tableData: [],
  currentPage: 1,
  totalPages: 0,
  perPage: DEFAULT_TABLE_PAGE_SIZE,
  searchField: '',
  isCreateModalOpen: false,
  unitsOfMeasure: [],
  regions: [],
  taskModalFieldLengths: taskModalFieldLengthValues,
};

// **** Types **** //

export interface TableDataRow {
  id: number;
  label: string;
  regions: string[];
  countries: (string | undefined)[];
  identifyBodyLocation: boolean;
  isMeasuredBy: string;
  description: string;
}

interface TaskModalFieldLength {
  maxTaskNameCharLength: number;
  maxDescriptionCharLength: number;
}

interface TaskConfigPageState {
  tableData: TableDataRow[];
  currentPage: number;
  totalPages: number;
  perPage: number;
  searchField: string;
  isCreateModalOpen: boolean;
  regions: Region[];
  unitsOfMeasure: MeasureUnit[];
  selectedTask?: TableDataRow;
  taskModalFieldLengths: TaskModalFieldLength;
}

// **** Functions **** //

/**
 * Convert task object to a table row
 */
const taskToTableRow = (task: Task): TableDataRow => {
  const tableRow: TableDataRow = {
    label: task.label,
    description: task.description ?? '',
    regions: task.regions?.map((r: Region) => r.name),
    countries: task.regions?.map((r: Region) => r.country?.name),
    isMeasuredBy: task.measureUnit?.name,
    identifyBodyLocation: task.identifyBodyLocation ? true : false,
  } as TableDataRow;
  if (task.id !== undefined) {
    tableRow.id = task.id;
  }
  return tableRow;
};

/**
 * Convert a table row to a task object
 */
const tableRowToTask = (tableRow: TableDataRow, regions: Region[], units: MeasureUnit[]): Task => {
  const task: Task = {
    label: tableRow.label,
    description: tableRow.description,
    identifyBodyLocation: tableRow.identifyBodyLocation,
    regions: tableRow.regions.map((rName: string) => regions.filter((r: Region) => rName === r.name)[0]),
    measureUnit: units.filter((unit: MeasureUnit) => tableRow.isMeasuredBy === unit.name)[0],
    // These fields need to be passed because backend requires it currently
    // will be removed later when backend fixed
    createdAt: '2022-02-25T23:06:47.000+00:00',
    updatedAt: new Date().toISOString(),
    id: tableRow.id !== undefined ? tableRow.id : '',
  } as Task;
  return task;
};

// **** Setup API Calls **** //

/**
 * Get all tasks
 */
export const fetchTasks = createAsyncThunk('tasks/fetchTasks', async () => {
  return TaskService.fetchTasks();
});

/**
 * Get units of measure
 */
export const fetchMeasureUnits = createAsyncThunk('tasks/fetchMeasureUnits', async () => {
  return TaskService.fetchMeasureUnits();
});

/**
 * Get regions
 */
export const fetchRegions = createAsyncThunk('tasks/fetchRegions', async () => {
  return TaskService.fetchRegions();
});

/**
 * Add or Edit a Task record
 */
export const addOrEditTask = createAsyncThunk('tasks/createOrUpdateTask', async (task: any, { getState }) => {
  const state: any = getState();
  const payload = tableRowToTask(task, state.taskConfigPage.regions, state.taskConfigPage.unitsOfMeasure);
  const respData = await TaskService.createOrUpdateTask(payload);
  if (respData) {
    payload.id = respData.id;
    return taskToTableRow(payload);
  }
});

// **** Setup **** //

const { reducer, actions } = createSlice({
  name: 'taskConfigPageSlice',
  initialState,
  reducers: {
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setIsCreateModalOpen: (state, action) => {
      state.isCreateModalOpen = action.payload;
    },
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTasks.fulfilled, (state, { payload }) => {
      state.tableData = payload.map((task: any) => taskToTableRow(task));
    });
    builder.addCase(fetchMeasureUnits.fulfilled, (state, { payload }) => {
      state.unitsOfMeasure = payload;
    });
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
    });
    builder.addCase(addOrEditTask.fulfilled, (state, { payload }) => {
      if (payload) {
        state.searchField = payload.label;
        const isAlreadyInTable = state.tableData.some((row) => {
          return row.id === payload.id;
        });
        // This will be the case when editing an existing site
        if (isAlreadyInTable) {
          state.tableData = state.tableData.map((row) => (row.id === payload.id ? payload : row));
          // This will be the case when adding a new site
        } else {
          state.tableData = [...state.tableData, payload];
        }
      }
    });
  },
});

// **** Export **** //

export const { setSearchField, setIsCreateModalOpen, setSelectedTask, setCurrentPage, setTotalPages, setPerPage } =
  actions;

export default reducer;
